/**
 * Incident objectives, shown in box 6 of the 202-CG and box 3 of the FEMA 202.
 *
 * Rows are rendered straight rather than through a table library: the list is
 * one incident's objectives, and the printed form fixes their order (type, then
 * when they were raised), so there is nothing for column sorting or pagination
 * to do.
 *
 * Editing follows the rest of the 202: one row is open at a time, a row added
 * but never named is dropped rather than saved blank, and deleting archives
 * straight away because there is no row left to hold a pending change on.
 */

import React, { useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Check,
  GripVertical,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { Empty, EmptyDescription, EmptyTitle } from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import {
  IncidentObjective,
  useCreateIncidentObjective,
  useEditIncidentObjective,
  useIncidentObjectives,
  useReorderIncidentObjectives,
} from './hooks/incidentObjectiveHooks';
import { moveObjective } from './objectiveOrder';
import { OBJECTIVES_LIBRARY } from './planningView/widgets/ostLibraryData';
import { INCIDENT } from './ICSConstants';
import {
  OBJECTIVE_TYPE_OPERATIONAL,
  incidentObjectiveTypeSegments,
  normalizeIncidentObjectiveType,
} from './incidentObjectiveTypes';

// Local type for table rows. We only need core fields for this UI.
type ObjectiveRow = Pick<
  IncidentObjective,
  'objective_id' | 'name' | 'archived' | 'strategies' | 'type'
> & {
  id: string; // stable id for table (objective_id for existing, custom for new)
  _isNew?: boolean;
  description?: string;
};

const NEW_ROW_ID = '__new__';

const OBJECTIVE_NAME_MAX_LENGTH = 200;

/** Not a real objective id, so a match against the library is unambiguous. */
const CREATE_TYPED_OBJECTIVE = '__create_typed__';

type ObjectiveTypeToggleProps = {
  value: string;
  disabled: boolean;
  onChange: (type: string) => void;
};

/** The O / M / O&M marker the 202-CG prints beside each objective. */
const ObjectiveTypeToggle: React.FC<ObjectiveTypeToggleProps> = ({
  value,
  disabled,
  onChange,
}) => (
  <ToggleGroup
    value={[value]}
    onValueChange={(next: unknown[]) => {
      const selected = next[0];
      // Base UI empties the array when the active item is pressed. The printed
      // form has no untyped objective, so a deselect is a no-op.
      if (typeof selected !== 'string') return;
      onChange(selected);
    }}
    variant="outline"
    size="sm"
    spacing={0}
    disabled={disabled}
    aria-label="Objective type"
  >
    {incidentObjectiveTypeSegments.map((seg) => (
      <Tooltip key={seg.value}>
        <ToggleGroupItem
          value={seg.value}
          aria-label={seg.tooltip}
          className="tw:px-2 tw:text-xs"
          render={<TooltipTrigger />}
        >
          {seg.abbrev}
        </ToggleGroupItem>
        <TooltipContent className="dark">{seg.tooltip}</TooltipContent>
      </Tooltip>
    ))}
  </ToggleGroup>
);

type ObjectiveNameEditorProps = {
  defaultValue: string;
  onSave: (name: string) => void;
  onCancel: () => void;
};

/** Inline editor for an existing objective's wording. */
const ObjectiveNameEditor: React.FC<ObjectiveNameEditorProps> = ({
  defaultValue,
  onSave,
  onCancel,
}) => {
  const [draft, setDraft] = useState(defaultValue);
  const trimmed = draft.trim();

  const save = () => {
    if (!trimmed) return;
    onSave(trimmed);
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-1">
      <Input
        autoFocus
        aria-label="Objective"
        maxLength={OBJECTIVE_NAME_MAX_LENGTH}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            save();
          }
          if (e.key === 'Escape') onCancel();
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label="Save objective"
        disabled={!trimmed}
        onClick={save}
      >
        <Check className="tw:size-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label="Cancel editing objective"
        onClick={onCancel}
      >
        <X className="tw:size-4" />
      </Button>
    </div>
  );
};

type NewObjectiveCellProps = {
  onCreate: (name: string) => void;
  onCancel: () => void;
};

/**
 * Add row: pull wording out of the objectives library, or type your own.
 *
 * Filtering is done here rather than by the combobox so the create entry
 * survives once the query stops matching any library label.
 */
/** Library labels are longer than the shared menu row, which is a fixed 32px. */
const objectiveOptionClassName =
  'tw:h-auto! tw:min-h-8 tw:items-start! tw:whitespace-normal tw:py-1.5 tw:text-left';

const NewObjectiveCell: React.FC<NewObjectiveCellProps> = ({
  onCreate,
  onCancel,
}) => {
  const [query, setQuery] = useState('');
  const trimmed = query.trim();

  const matches = useMemo(() => {
    if (!trimmed) return OBJECTIVES_LIBRARY;
    const needle = trimmed.toLowerCase();
    return OBJECTIVES_LIBRARY.filter((option) =>
      option.label.toLowerCase().includes(needle)
    );
  }, [trimmed]);

  const handleSelect = (value: unknown) => {
    if (typeof value !== 'string' || !value) return;
    if (value === CREATE_TYPED_OBJECTIVE) {
      onCreate(trimmed);
      return;
    }
    const picked = OBJECTIVES_LIBRARY.find((option) => option.value === value);
    if (picked) onCreate(picked.label);
  };

  const save = () => {
    if (!trimmed) return;
    onCreate(trimmed);
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-1">
      <div className="tw:min-w-0 tw:flex-1">
        <Combobox
          value={null}
          filter={null}
          // Puts the highlight on the "+ Create" entry as soon as there is
          // text. Base UI only acts on Enter when something is highlighted, so
          // without this a typed objective is dropped when the list closes.
          autoHighlight
          inputValue={query}
          onInputValueChange={(next) => setQuery(String(next ?? ''))}
          onValueChange={handleSelect}
        >
          <ComboboxInput
            autoFocus
            aria-label="Select or create Objective"
            placeholder="Select or create Objective..."
            className="tw:w-full"
            maxLength={OBJECTIVE_NAME_MAX_LENGTH}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onCancel();
            }}
          />
          <ComboboxContent className="dark">
            <ComboboxList>
              {trimmed ? (
                <ComboboxItem
                  className={objectiveOptionClassName}
                  value={CREATE_TYPED_OBJECTIVE}
                >
                  <span className="tw:min-w-0 tw:flex-1 tw:whitespace-normal">
                    + Create “{trimmed}”
                  </span>
                </ComboboxItem>
              ) : null}
              {matches.map((option) => (
                <ComboboxItem
                  key={option.value}
                  className={objectiveOptionClassName}
                  value={option.value}
                >
                  <span className="tw:min-w-0 tw:flex-1 tw:whitespace-normal">
                    {option.label}
                  </span>
                </ComboboxItem>
              ))}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label="Save objective"
        className="tw:shrink-0"
        disabled={!trimmed}
        onClick={save}
      >
        <Check className="tw:size-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label="Cancel adding objective"
        className="tw:shrink-0"
        onClick={onCancel}
      >
        <X className="tw:size-4" />
      </Button>
    </div>
  );
};

type SortableObjectiveRowProps = {
  id: string;
  label: string;
  disabled: boolean;
  children: (grip: React.ReactNode) => React.ReactNode;
};

/**
 * A draggable row.
 *
 * The grip is handed back to the caller rather than applied here because the
 * row is full of inputs, toggles and buttons: putting the drag listeners on the
 * row itself would swallow the clicks those controls need.
 */
const SortableObjectiveRow: React.FC<SortableObjectiveRowProps> = ({
  id,
  label,
  disabled,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const grip = disabled ? null : (
    <button
      type="button"
      className="tw:cursor-grab tw:text-muted-foreground tw:active:cursor-grabbing"
      aria-label={`Reorder ${label}`}
      title="Drag to reorder (or use arrow keys)"
      {...attributes}
      {...listeners}
    >
      <GripVertical className="tw:size-4" />
    </button>
  );

  return (
    <TableRow
      ref={setNodeRef}
      // Translate rather than the full transform: dnd-kit's includes a scale,
      // which stretches table cells out of line with the header.
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn('tw:relative', isDragging && 'tw:opacity-40')}
    >
      {children(grip)}
    </TableRow>
  );
};

type IncidentObjectivesTableProps = {
  readOnly?: boolean;
  /** Fill for the table, so it sits flush on whichever card hosts it. */
  className?: string;
};

const IncidentObjectivesTable: React.FC<IncidentObjectivesTableProps> = ({
  readOnly = false,
  className,
}) => {
  // TODO: No stable sort right now. adding new items to the top of the list causes confusion.
  const { data: liveObjectives = [] } = useIncidentObjectives(INCIDENT, (i) =>
    i.filter((o) => !o.archived)
  );

  const createObjectiveMutation = useCreateIncidentObjective();
  const editObjectiveMutation = useEditIncidentObjective();
  const reorderMutation = useReorderIncidentObjectives();

  const [query, setQuery] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newRowType, setNewRowType] = useState(OBJECTIVE_TYPE_OPERATIONAL);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const onAddClick = () => {
    setIsAdding(true);
  };

  const onCancelAdd = () => {
    setIsAdding(false);
    setNewRowType(OBJECTIVE_TYPE_OPERATIONAL);
  };

  const saveObjective = useMemo(
    () =>
      async (
        row: ObjectiveRow,
        updates: Partial<Pick<IncidentObjective, 'name' | 'archived' | 'type'>>
      ) => {
        const isCreate =
          !row.objective_id || row._isNew || row.id === NEW_ROW_ID;
        const trimmedName = updates.name?.trim();

        if (isCreate) {
          if (!trimmedName) return;
          const type = newRowType;
          // The list shows the new row from the optimistic cache. Waiting here
          // left the add row in place until the request finished.
          setIsAdding(false);
          setNewRowType(OBJECTIVE_TYPE_OPERATIONAL);
          void createObjectiveMutation
            .mutateAsync({
              name: trimmedName,
              description: undefined,
              type,
            })
            .catch(() => undefined);
          return;
        }

        const nextType =
          updates.type !== undefined
            ? updates.type
            : normalizeIncidentObjectiveType(row.type);

        editObjectiveMutation.mutate({
          name: trimmedName ?? row.name,
          archived: updates.archived ?? row.archived,
          objective_id: row.objective_id,
          strategies: row.strategies || [],
          type: nextType,
          description: row.description,
        });
        setEditingId(null);
      },
    [createObjectiveMutation, editObjectiveMutation, newRowType]
  );

  const filtered = useMemo(() => {
    const base = liveObjectives;
    if (!query) return base;
    const q = query.toLowerCase();
    return base.filter((o) => (o.name || '').toLowerCase().includes(q));
  }, [liveObjectives, query]);

  const tableData: ObjectiveRow[] = useMemo(() => {
    const rows = filtered.map<ObjectiveRow>((o) => ({
      id: String(o.objective_id || o.id),
      objective_id: o.objective_id,
      name: o.name,
      archived: o.archived,
      strategies: (o as any).strategies || [],
      type: normalizeIncidentObjectiveType(o.type),
      description: (o as any).description,
    }));
    if (!isAdding) return rows;
    return [
      {
        id: NEW_ROW_ID,
        objective_id: '' as any,
        name: '',
        archived: false,
        _isNew: true,
        strategies: [],
        type: newRowType,
      },
      ...rows,
    ];
  }, [filtered, isAdding, newRowType]);

  const selectedRows = useMemo(
    () =>
      tableData.filter(
        (row) => !row._isNew && !!row.objective_id && selectedIds.has(row.id)
      ),
    [selectedIds, tableData]
  );

  const deleteSelected = async () => {
    if (!selectedRows.length) return;

    await Promise.all(
      selectedRows.map((row) =>
        editObjectiveMutation.mutateAsync({
          name: row.name,
          archived: true,
          ['objective_id']: row.objective_id,
          strategies: row.strategies || [],
          type: normalizeIncidentObjectiveType(row.type),
          description: row.description,
        })
      )
    );

    setSelectedIds(new Set());
  };

  const visibleIds = useMemo(
    () => tableData.filter((r) => !r._isNew).map((r) => r.id),
    [tableData]
  );

  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected = visibleIds.some((id) => selectedIds.has(id));

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) {
        return new Set([...prev].filter((id) => !visibleIds.includes(id)));
      }
      return new Set([...prev, ...visibleIds]);
    });
  };

  const toggleRow = (rowId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const savedRows = useMemo(
    () => tableData.filter((row) => !row._isNew),
    [tableData]
  );
  const sortableIds = useMemo(
    () => savedRows.map((row) => row.id),
    [savedRows]
  );

  // A search hides rows, and renumbering only what is on screen would shuffle
  // the objectives it filtered out. Reordering waits until the full list shows.
  const canReorder = !readOnly && !query;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const reordered = moveObjective(
      sortableIds,
      String(active.id),
      String(over.id)
    );
    if (reordered === sortableIds) return;

    const objectiveIdByRowId = new Map(
      savedRows.map((row) => [row.id, row.objective_id])
    );
    reorderMutation.mutate(
      reordered
        .map((rowId) => objectiveIdByRowId.get(rowId))
        .filter((id): id is IncidentObjective['objective_id'] => Boolean(id))
    );
  };

  return (
    <TooltipProvider>
      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <InputGroup className="tw:max-w-xs" data-tour="ics201-oa-search">
            <InputGroupAddon>
              <Search className="tw:size-4" />
            </InputGroupAddon>
            <InputGroupInput
              aria-label="Search objectives"
              placeholder="Search objectives..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </InputGroup>

          {!readOnly && (
            <Button
              type="button"
              size="sm"
              className="tw:shrink-0"
              disabled={isAdding}
              onClick={onAddClick}
              data-tour="ics201-add-objective"
            >
              <Plus className="tw:size-4" />
              Add Objective
            </Button>
          )}
        </div>

        {!readOnly && selectedRows.length > 0 && (
          <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
            <div>{selectedRows.length} selected</div>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={editObjectiveMutation.isPending}
              onClick={(e) => {
                e.stopPropagation();
                void deleteSelected();
              }}
            >
              <Trash2 className="tw:size-4" />
              Delete Selected
            </Button>
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            <Table className={cn(className)}>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10" />
                  <TableHead className="tw:w-16">
                    <Checkbox
                      aria-label="Select all objectives"
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead className="tw:w-36" data-tour="ics201-objective-type">
                    Type
                  </TableHead>
                  <TableHead>Objective</TableHead>
                  <TableHead className="tw:w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Empty className="tw:p-6">
                        <EmptyTitle>No objectives yet</EmptyTitle>
                        <EmptyDescription>
                          {`Click "Add Objective" to create your first objective`}
                        </EmptyDescription>
                      </Empty>
                    </TableCell>
                  </TableRow>
                ) : (
                  tableData.map((row) => {
                    const isEditing = editingId === row.id;
                    const cells = (grip: React.ReactNode) => (
                      <>
                        <TableCell className="tw:align-middle">
                          {grip}
                        </TableCell>
                        <TableCell className="tw:align-middle">
                          {row._isNew ? null : (
                            <Checkbox
                              aria-label={`Select ${row.name}`}
                              checked={selectedIds.has(row.id)}
                              onCheckedChange={() => toggleRow(row.id)}
                            />
                          )}
                        </TableCell>
                        <TableCell className="tw:align-middle">
                          <ObjectiveTypeToggle
                            value={normalizeIncidentObjectiveType(row.type)}
                            disabled={readOnly}
                            onChange={(type) => {
                              if (row._isNew) {
                                setNewRowType(type);
                                return;
                              }
                              void saveObjective(row, { type });
                            }}
                          />
                        </TableCell>
                        <TableCell className="tw:align-middle tw:whitespace-normal">
                          {row._isNew ? (
                            <NewObjectiveCell
                              onCreate={(name) =>
                                void saveObjective(row, { name })
                              }
                              onCancel={onCancelAdd}
                            />
                          ) : isEditing ? (
                            <ObjectiveNameEditor
                              defaultValue={row.name || ''}
                              onSave={(name) =>
                                void saveObjective(row, { name })
                              }
                              onCancel={() => setEditingId(null)}
                            />
                          ) : (
                            <span>{row.name}</span>
                          )}
                        </TableCell>
                        <TableCell className="tw:align-middle">
                          {readOnly || row._isNew ? null : (
                            <div className="tw:flex tw:justify-end tw:gap-1">
                              {isEditing ? null : (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  aria-label={`Edit ${row.name}`}
                                  onClick={() => setEditingId(row.id)}
                                >
                                  <Pencil className="tw:size-4" />
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-label={`Delete ${row.name}`}
                                onClick={() => {
                                  void saveObjective(row, { archived: true });
                                }}
                              >
                                <Trash2 className="tw:size-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </>
                    );

                    // The add row has nothing saved to reorder against, so it stays
                    // put at the top until it is committed.
                    if (row._isNew) {
                      return <TableRow key={row.id}>{cells(null)}</TableRow>;
                    }

                    return (
                      <SortableObjectiveRow
                        key={row.id}
                        id={row.id}
                        label={row.name}
                        disabled={!canReorder}
                      >
                        {cells}
                      </SortableObjectiveRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </SortableContext>
        </DndContext>

        <div className="tw:text-sm tw:text-muted-foreground">
          {filtered.length} objectives
        </div>
      </div>
    </TooltipProvider>
  );
};

export default IncidentObjectivesTable;
